import { useState, useEffect, useMemo, useRef } from 'react';
import { Tree, TreeSelectionEvent, TreeCheckboxSelectionKeys, TreeMultipleSelectionKeys } from 'primereact/tree';
import { TreeNode } from 'primereact/treenode';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import accessRightService from './access-right-service';
import { Fieldset } from 'primereact/fieldset';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { useLocation, useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { InputSwitch } from 'primereact/inputswitch';

interface DropdownItem {
    referenceName: string;
    referenceCode: string;
    fullDesc: string;
    objectId: string;
}

const AccessRightEdit: React.FC = () => {
    const location = useLocation();
    const accessRightItem = location.state?.selectedItem;
    const navigate = useNavigate();
    const [clientState, setClientState] = useState<any>(null);

    const [treeNodes, setTreeNodes] = useState<TreeNode[]>([]);
    const [formData, setFormData] = useState<any>({});
    const [menuSetDropdown, setMenuSetDropdown] = useState<DropdownItem[]>([]);
    const [selectedNode, setSelectedNode] = useState<any>(null);
    const [selectedNodeJson, setSelectedNodeJson] = useState<any>(null);
    const [departmentDropdown, setModuleDropdown] = useState<DropdownItem[]>([]);
    const toast = useRef<Toast>(null);
    const [accessRight, setAccessRight] = useState<any>(null);

    const menuSetDropdownOptions = useMemo(() => menuSetDropdown, [menuSetDropdown]);
    const departmentDropdownOptions = useMemo(() => departmentDropdown, [departmentDropdown]);

    const defaultData = {
        accessRoleId: null,
        recordStatus: true,
        menuSetObjectId: "[]"
    }

    useEffect(() => {
        const fetchClientState = async () => {
            try {
                accessRightService.getClientState().then((data) => {
                    accessRightService.getModuleDropdown().then((data) => {
                        setModuleDropdown(data);
                    });
                    setClientState(data);
                });

                accessRightService.getMenuSetDropdownByObjectType('M').then((data) => {
                    setMenuSetDropdown(data);
                });

                if (accessRightItem !== undefined) {
                    accessRightItem.recordStatus = accessRightItem.recordStatus == '1' ? true : false;
                    accessRightService.getADPMenuTreeAccess(accessRightItem.menuSetObjectId).then((data) => {
                        const menuTree: TreeNode[] = data;
                        setTreeNodes(menuTree);
                        setSelectedNode(JSON.parse(accessRightItem.accessMenuTree));
                        setSelectedNodeJson(accessRightItem.accessMenuTree);
                        setFormData({
                            ...formData,
                            objectId: accessRightItem.menuSetObjectId
                        });

                    });

                    setAccessRight(accessRightItem);
                } else {
                    setAccessRight(defaultData);
                };


            } catch (error) {
                console.error('Error fetching client state:', error);
            }
        };

        fetchClientState();
    }, []);

    const handleSelectionChange = (event: TreeSelectionEvent) => {
        const selectedKeys = event.value as TreeCheckboxSelectionKeys | null;
        setSelectedNode(selectedKeys);
        setSelectedNodeJson(JSON.stringify(event.value));
    };

    const handleMenusetDropdownChange = (e: any) => {

        const { id, value } = e.target;
        accessRightService.getADPMenuTreeAccess(value).then((data) => {
            const menuTree: TreeNode[] = data;
            setTreeNodes(menuTree);
            setFormData({
                ...formData,
                [id]: value
            });

        });
    };

    const handleInputChange = (e: any) => {
        const { id, value } = e.target;
        setAccessRight({
            ...accessRight,
            [id]: value
        });
    };

    const handleDropdownChange = (e: any) => {
        const { id, value } = e.target;
        setAccessRight({
            ...accessRight,
            [id]: value
        });
    };

    const treeTemplate = () => {
        function submit(): void {
            const currentDate = new Date().toLocaleString("en-US", { timeZone: clientState.timeZone });

            accessRight.accessMenuTree = selectedNodeJson;
            accessRight.modifiedDate = new Date(currentDate);
            accessRight.modifiedBy = "ADMIN";
            accessRight.recordStatus = accessRight.recordStatus == true ? '1' : '0';
            accessRight.menuSetObjectId = formData.objectId;

            if (accessRight.accessRoleId === null) {

                accessRight.insertDate = new Date(currentDate);
                accessRight.insertBy = "ADMIN";
                accessRight.facilityCode = clientState.facilityCode;

                accessRightService.create(accessRight).then(() => {
                    clientState.success.detail = 'Already Save Record';
                    toast.current?.show(clientState.success);
                    navigate("/admin/access-right");

                }).catch((error) => {
                    const errorMessage = error.response?.data?.message || 'Failed to create record.';
                    clientState.error.detail = errorMessage;
                    toast.current?.show(clientState.error);
                });
            } else {
                accessRightService.update(accessRight).then(() => {
                    clientState.success.detail = 'Already Save Record';
                    toast.current?.show(clientState.success);
                    navigate("/admin/access-right");

                }).catch((error) => {
                    const errorMessage = error.response?.data?.message || 'Failed to create record.';
                    clientState.error.detail = errorMessage;
                    toast.current?.show(clientState.error);
                });
            }




        }

        function handleBack(): void {
            navigate(`/admin/access-right`);
        }

        return (
            <>
                <Toast ref={toast} />
                <Button label="Save" icon="pi pi-check" style={{ marginRight: '.5em' }} className="p-button-success" onClick={submit} />
                <Button label="Back" icon="pi pi-trash" style={{ marginRight: '.5em' }} onClick={() => handleBack()} />

            </>
        );
    };

    return (
        <div className="grid">
            <div className="col-6">
                <div className="card">
                    <Fieldset legend="Menu Tree">
                        <div className="p-fluid form-grid grid">
                            <div className="field col-12 md:col-12">
                                <label htmlFor="objectId">Menu set</label>
                                <Dropdown
                                    id="objectId"
                                    value={formData?.objectId || ''}
                                    onChange={handleMenusetDropdownChange}
                                    options={menuSetDropdownOptions}
                                    optionLabel="fullDesc"
                                    placeholder="Select One"
                                    optionValue="objectId"
                                >
                                </Dropdown>
                            </div>
                        </div>
                        <Tree
                            value={treeNodes || []}
                            selectionMode="checkbox"
                            selectionKeys={selectedNode}
                            onSelectionChange={handleSelectionChange}
                        />
                    </Fieldset>
                </div>
            </div>
            <div className="col-6">
                <div className="card">
                    <Fieldset legend="Menu Information">
                        <div className="p-fluid form-grid grid">
                            <div className="field col-12 md:col-12">
                                <label>Full Name</label>
                                <InputText
                                    id="fullDesc"
                                    value={accessRight?.fullDesc || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="field col-12 md:col-12">
                                <label htmlFor="departmentCode">Menu set</label>
                                <Dropdown
                                    id="departmentCode"
                                    value={accessRight?.departmentCode || ''}
                                    onChange={handleDropdownChange}
                                    options={departmentDropdownOptions}
                                    optionLabel="fullDesc"
                                    placeholder="Select One"
                                    optionValue="departmentCode"
                                >
                                </Dropdown>
                            </div>
                            <div className="field col-12 md:col-6">
                                <h6>Status</h6>
                                <InputSwitch
                                    checked={accessRight?.recordStatus || ''}
                                    onChange={(e) => setAccessRight({ ...accessRight, recordStatus: e.value })}
                                />
                            </div>
                        </div>
                    </Fieldset>
                    <div>
                        <br />
                    </div>
                    <Toolbar end={treeTemplate}></Toolbar>
                </div>
            </div>
        </div>

    );
};

export default AccessRightEdit;
